import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Alert, Badge, Button, Spinner } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { API_BASE_CANDIDATES, SITE_BASE_CANDIDATES } from '../../apiConfig';
import resolveStudentResultContent from './studentResultContent';
import StudentThemeToggle from './StudentThemeToggle';
import sanitizeHtml from '../../helpers/sanitizeHtml';

const TEST_ROUTE = '/authentication/basic/student-test';
const LOGIN_ROUTE = '/authentication/basic/login';

const RESULT_SCORE_DISABLED_KEYS = ['temperament-test', 'kommunikativ'];

const formatClock = totalSeconds => {
  const safeSeconds = Math.max(0, Number(totalSeconds) || 0);
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const formatResultNumber = value => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return String(value);
  }

  if (Math.floor(numeric) === numeric) {
    return String(numeric);
  }

  return numeric.toFixed(2);
};

const formatCompletedAt = result => {
  if (!result) {
    return '';
  }

  const datePart = String(result.finished_date || '').trim();
  const timePart = String(result.finished_time || '').trim();

  return [datePart, timePart].filter(Boolean).join(' ');
};

const toneIcon = tone => {
  switch (tone) {
    case 'success':
      return 'check-circle';
    case 'info':
      return 'bullseye';
    case 'warning':
      return 'exclamation-triangle';
    case 'danger':
      return 'exclamation-triangle';
    default:
      return 'circle';
  }
};

const normalizeRichHtml = (value, siteBase) => {
  const baseUrl = `${siteBase.replace(/\/+$/, '')}/student-assets/`;
  return sanitizeHtml(value, { baseUrl, allowDataImages: true });
};

const buildQuestionState = (payload, fallback = {}) => {
  const options = [
    { key: 'A', value: payload.sh1, html: payload.v1 },
    { key: 'B', value: payload.sh2, html: payload.v2 },
    { key: 'C', value: payload.sh3, html: payload.v3 },
    { key: 'D', value: payload.sh4, html: payload.v4 }
  ].filter(option => option.value && String(option.html || '').trim() !== '');

  return {
    questionNumber: Number(payload.soni) || Number(fallback.questionNumber) || 1,
    totalQuestions: Number(payload.testSoni) || Number(fallback.totalQuestions) || options.length,
    subject: payload.fan || fallback.subject || '',
    group: payload.guruh || fallback.group || '',
    passport: payload.pasport || fallback.passport || '',
    studentName: payload.fio || fallback.studentName || '',
    image: payload.rasm || fallback.image || '',
    questionHtml: payload.savol || '',
    options
  };
};

const StudentTestPage = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [exitPending, setExitPending] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [archived, setArchived] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [resultError, setResultError] = useState('');
  const [resultLoading, setResultLoading] = useState(false);
  const [completedResult, setCompletedResult] = useState(null);
  const [questionState, setQuestionState] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(null);

  const siteBase = SITE_BASE_CANDIDATES[0] || 'https://psixologiya.suvoriy.com/';
  const defaultImage = `${siteBase}/rasm/talaba/logo.png`;

  const loadEndpointCandidates = useMemo(
    () => API_BASE_CANDIDATES.map(base => `${base}/test/question`),
    []
  );

  const submitEndpointCandidates = useMemo(
    () => API_BASE_CANDIDATES.map(base => `${base}/test/answer`),
    []
  );

  const logoutEndpointCandidates = useMemo(
    () => API_BASE_CANDIDATES.map(base => `${base}/test/logout`),
    []
  );

  const resultEndpointCandidates = useMemo(
    () => API_BASE_CANDIDATES.map(base => `${base}/test/result`),
    []
  );

  const submitRequest = async (candidates, buildRequest) => {
    let lastError = null;

    for (const endpoint of candidates) {
      try {
        return await buildRequest(endpoint);
      } catch (error) {
        lastError = error;
        const status = error.response?.status || 0;
        if (status && ![404, 502, 503, 504].includes(status)) {
          throw error;
        }
      }
    }

    throw lastError;
  };

  const resolveStudentImage = imagePath => {
    if (!imagePath || typeof imagePath !== 'string') {
      return defaultImage;
    }

    if (/^https?:\/\//i.test(imagePath)) {
      return imagePath;
    }

    return `${siteBase}/rasm/talaba/${imagePath.replace(/^\/+/, '')}`;
  };

  const handlePortalExit = async redirectPath => {
    setExitPending(true);

    try {
      await submitRequest(logoutEndpointCandidates, endpoint =>
        axios.post(
          endpoint,
          {},
          {
            timeout: 10000,
            withCredentials: true
          }
        )
      );
    } catch (error) {
      // Logout is best-effort only.
    } finally {
      history.replace(redirectPath);
      setExitPending(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const loadQuestion = async () => {
      setLoading(true);
      setErrorMessage('');
      setResultError('');
      setArchived(false);
      setCompleted(false);
      setCompletedResult(null);

      try {
        const response = await submitRequest(loadEndpointCandidates, endpoint => {
          const body = new URLSearchParams();
          return axios.post(endpoint, body, {
            timeout: 15000,
            withCredentials: true
          });
        });

        if (!isMounted) {
          return;
        }

        const payload = response.data || {};

        if (payload.chiqish) {
          setCompletedResult(payload.result || null);
          setCompleted(true);
          setQuestionState(null);
          setSecondsLeft(0);
          return;
        }

        if (!payload.malumot) {
          throw new Error(
            payload.message || "Test savolini yuklashda ma`lumot topilmadi."
          );
        }

        const nextQuestionState = buildQuestionState(payload);
        setQuestionState(nextQuestionState);
        setSecondsLeft(Math.max(0, (Number(payload.testVaqti) || 0) * 60));
        setSelectedVariant('');
      } catch (error) {
        if (!isMounted) {
          return;
        }

        if (error.response?.data?.auth_required) {
          history.replace(LOGIN_ROUTE);
          return;
        }

        const message =
          error.response?.data?.message ||
          error.message ||
          "Testni yuklashda xatolik yuz berdi.";
        setErrorMessage(message);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadQuestion();

    return () => {
      isMounted = false;
    };
  }, [history, loadEndpointCandidates]);

  useEffect(() => {
    if (!completed || completedResult) {
      return undefined;
    }

    let active = true;

    const loadResult = async () => {
      setResultLoading(true);
      setResultError('');

      try {
        const response = await submitRequest(resultEndpointCandidates, endpoint => {
          const body = new URLSearchParams();
          return axios.post(endpoint, body, {
            timeout: 15000,
            withCredentials: true
          });
        });

        if (!active) {
          return;
        }

        const payload = response.data || {};

        if (!payload.success || !payload.result) {
          throw new Error(payload.message || "Natija ma'lumotlari yuklanmadi.");
        }

        setCompletedResult(payload.result);
      } catch (error) {
        if (!active) {
          return;
        }

        if (error.response?.data?.auth_required) {
          history.replace(LOGIN_ROUTE);
          return;
        }

        const message =
          error.response?.data?.message ||
          error.message ||
          "Natija ma'lumotlarini yuklashda xatolik yuz berdi.";
        setResultError(message);
      } finally {
        if (active) {
          setResultLoading(false);
        }
      }
    };

    loadResult();

    return () => {
      active = false;
    };
  }, [completed, completedResult, history, resultEndpointCandidates]);

  useEffect(() => {
    if (loading || completed || archived || secondsLeft === null || secondsLeft <= 0) {
      return undefined;
    }

    const timerId = window.setInterval(() => {
      setSecondsLeft(current => {
        if (current === null || current <= 0) {
          window.clearInterval(timerId);
          return 0;
        }

        return current - 1;
      });
    }, 1000);

    return () => {
      window.clearInterval(timerId);
    };
  }, [archived, completed, loading, secondsLeft]);

  const handleSubmit = async event => {
    event.preventDefault();

    if (!selectedVariant) {
      toast.info('Variantlardan birini tanlang');
      return;
    }

    if (!questionState) {
      return;
    }

    setSubmitting(true);
    setErrorMessage('');

    try {
      const response = await submitRequest(submitEndpointCandidates, endpoint => {
        const body = new URLSearchParams();
        body.set('variant', selectedVariant);
        body.set('vaqt', String(Math.max(0, Math.ceil((secondsLeft || 0) / 60))));
        body.set('soni', String(questionState.questionNumber));
        body.set('guruh', questionState.group);
        return axios.post(endpoint, body, {
          timeout: 15000,
          withCredentials: true
        });
      });

      const payload = response.data || {};

      if (payload.arxiv) {
        setArchived(true);
        setSelectedVariant('');
        toast.error('Siz ushbu testdan chetlashtirilgansiz');
        return;
      }

      if (payload.bush) {
        toast.info('Variantlardan birini tanlang');
        return;
      }

      if (payload.chiqish) {
        setCompletedResult(payload.result || null);
        setCompleted(true);
        setSelectedVariant('');
        setQuestionState(current => current && { ...current });
        toast.success('Test muvaffaqiyatli yakunlandi');
        return;
      }

      if (!payload.malumot) {
        throw new Error(payload.message || "Savolni yangilashda xatolik yuz berdi.");
      }

      setQuestionState(current => buildQuestionState(payload, current || questionState));
      setSelectedVariant('');
    } catch (error) {
      if (error.response?.data?.auth_required) {
        history.replace(LOGIN_ROUTE);
        return;
      }

      const message =
        error.response?.data?.message ||
        error.message ||
        "Testni yuborishda xatolik yuz berdi.";
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const rawQuestionHtml = questionState ? questionState.questionHtml : '';
  const questionHtml = useMemo(
    () => normalizeRichHtml(rawQuestionHtml, siteBase),
    [rawQuestionHtml, siteBase]
  );

  const normalizedOptions = useMemo(
    () => {
      const rawOptions = questionState ? questionState.options : [];

      return rawOptions.map(option => ({
        ...option,
        html: normalizeRichHtml(option.html, siteBase)
      }));
    },
    [questionState, siteBase]
  );

  const currentPhoto = resolveStudentImage(questionState?.image || '');
  const progressPercent = questionState
    ? Math.min(100, Math.max(0, (questionState.questionNumber / questionState.totalQuestions) * 100))
    : 0;
  const finalQuestion =
    !!questionState && questionState.questionNumber >= questionState.totalQuestions;
  const completedContent = completedResult ? resolveStudentResultContent(completedResult) : null;
  const completedPhoto = resolveStudentImage(completedResult?.image || '');
  const completedAt = formatCompletedAt(completedResult);
  const completedScoreVisible =
    !!completedResult &&
    completedResult.ball !== null &&
    completedResult.ball !== undefined &&
    !RESULT_SCORE_DISABLED_KEYS.includes(completedResult.kalit);
  const completedMetrics = completedResult
    ? [
        {
          icon: 'book',
          label: 'Test',
          value: completedResult.subject || 'Test'
        },
        {
          icon: 'users',
          label: 'Guruh',
          value: completedResult.group || '-'
        },
        {
          icon: 'clipboard-list',
          label: 'Savollar',
          value:
            completedResult.total_questions > 0
              ? `${completedResult.answered_count || completedResult.total_questions}/${completedResult.total_questions}`
              : String(completedResult.answered_count || 0)
        },
        {
          icon: toneIcon(completedContent?.tone),
          label: 'Natija',
          value: completedContent?.label || 'Natija saqlandi'
        },
        ...(completedContent?.resultCode
          ? [
              {
                icon: 'id-card',
                label: 'Kod',
                value: completedContent.resultCode
              }
            ]
          : []),
        ...(completedScoreVisible
          ? [
              {
                icon: 'chart-line',
                label: 'Ball',
                value: `${formatResultNumber(completedResult.ball)} ball`
              }
            ]
          : []),
        ...(completedAt
          ? [
              {
                icon: 'clock',
                label: 'Saqlangan vaqt',
                value: completedAt
              }
            ]
          : [])
      ]
    : [];

  return (
    <div className="psi-auth-stack psi-auth-stack--student-test">
      <div className="psi-login-panel psi-login-panel--passport psi-login-panel--test">
        <div className="psi-student-test__topbar">
          <div className="psi-student-test__intro">
            <div className="psi-student-passport__eyebrow">Talaba uchun</div>
            <h1 className="psi-student-test__title">Onlayn test muhiti</h1>
            <p className="psi-student-test__subtitle">
              Savollar ketma-ket ko&apos;rinadi. Variantni tanlang va keyingi savolga o&apos;ting.
            </p>
          </div>

          <div className="psi-student-test__topbar-tools">
            <StudentThemeToggle />
            {!completed ? (
              <div className="psi-student-test__actions">
                <Button
                  color="light"
                  className="psi-student-passport__ghost"
                  onClick={() => handlePortalExit(LOGIN_ROUTE)}
                  disabled={exitPending || submitting}
                >
                  Chiqish
                </Button>
              </div>
            ) : null}
          </div>
        </div>

        {errorMessage ? (
          <Alert color="danger" className="psi-login-alert mt-3">
            {errorMessage}
          </Alert>
        ) : null}

        {loading ? (
          <div className="psi-student-test__loading">
            <Spinner color="light" />
            <span>Test savollari yuklanmoqda...</span>
          </div>
        ) : null}

        {!loading && completed ? (
          resultLoading && !completedResult ? (
            <div className="psi-student-test__state-card">
              <div className="psi-student-test__state-icon psi-student-test__state-icon--success">
                <Spinner color="light" />
              </div>
              <h2 className="psi-student-test__state-title">Natija tayyorlanmoqda</h2>
              <p className="psi-student-test__state-text">
                Test yakunlandi. Yakuniy xulosa va ko&apos;rsatkichlar yuklanmoqda.
              </p>
            </div>
          ) : completedResult && completedContent ? (
            <div className={`psi-student-result psi-student-result--${completedContent.tone || 'secondary'}`}>
              <div className="psi-student-result__hero">
                <div className="psi-student-result__profile">
                  <div className="psi-student-result__photo">
                    <img
                      src={completedPhoto}
                      alt="Talaba rasmi"
                      onError={({ currentTarget }) => {
                        currentTarget.onerror = null;
                        currentTarget.src = defaultImage;
                      }}
                    />
                  </div>

                  <div className="psi-student-result__profile-copy">
                    <span className="psi-student-result__eyebrow">{completedContent.eyebrow}</span>
                    <h2 className="psi-student-result__title">
                      {completedContent.label || 'Test natijasi'}
                    </h2>
                    <p className="psi-student-result__summary">{completedContent.summary}</p>
                    <div className="psi-student-result__chips">
                      <span className={`psi-student-result__status psi-student-result__status--${completedContent.tone || 'secondary'}`}>
                        <FontAwesomeIcon icon={toneIcon(completedContent.tone)} />
                        <span>{completedContent.label}</span>
                      </span>
                      {completedContent.resultCode ? (
                        <span className="psi-student-result__status psi-student-result__status--dark">
                          <FontAwesomeIcon icon="id-card" />
                          <span>{completedContent.resultCode}</span>
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="psi-student-result__hero-side">
                  <div className="psi-student-result__student-name">
                    {completedResult.student_name || 'Talaba'}
                  </div>
                  <div className="psi-student-result__hero-meta">
                    <span className="psi-student-result__hero-pill">
                      <FontAwesomeIcon icon="book" />
                      <span>{completedResult.subject || 'Test'}</span>
                    </span>
                    <span className="psi-student-result__hero-pill">
                      <FontAwesomeIcon icon="users" />
                      <span>{completedResult.group || 'Guruh mavjud emas'}</span>
                    </span>
                  </div>
                  <p className="psi-student-result__guidance">{completedContent.guidance}</p>
                </div>
              </div>

              <div className="psi-student-result__metrics">
                {completedMetrics.map(metric => (
                  <div key={`${metric.label}-${metric.value}`} className="psi-student-result__metric">
                    <span className="psi-student-result__metric-icon">
                      <FontAwesomeIcon icon={metric.icon} />
                    </span>
                    <div className="psi-student-result__metric-copy">
                      <strong>{metric.value}</strong>
                      <span>{metric.label}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="psi-student-result__layout">
                <div className="psi-student-result__panel">
                  <div className="psi-student-result__panel-head">
                    <span className="psi-student-result__panel-kicker">Asosiy xulosa</span>
                    <h3>{completedContent.label}</h3>
                  </div>
                  <p className="psi-student-result__description">{completedContent.description}</p>

                  {completedContent.recommendations && completedContent.recommendations.length ? (
                    <div className="psi-student-result__section">
                      <h4>Tavsiyalar</h4>
                      <ul className="psi-student-result__list">
                        {completedContent.recommendations.map(item => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>

                <div className="psi-student-result__panel psi-student-result__panel--aside">
                  {completedContent.careers && completedContent.careers.length ? (
                    <div className="psi-student-result__section">
                      <h4>Mos yo&apos;nalishlar</h4>
                      <div className="psi-student-result__career-grid">
                        {completedContent.careers.map(item => (
                          <span key={item} className="psi-student-result__career-pill">
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  <div className="psi-student-result__section psi-student-result__section--note">
                    <h4>Eslatma</h4>
                    <p>{completedContent.motivationNote}</p>
                  </div>

                  <div className="psi-student-result__actions">
                    <Button
                      color="primary"
                      className="psi-student-result__button"
                      onClick={() => handlePortalExit(LOGIN_ROUTE)}
                      disabled={exitPending}
                    >
                      Talaba kirishiga qaytish
                    </Button>
                  </div>
                </div>
              </div>

              {resultError ? (
                <Alert color="warning" className="mb-0">
                  {resultError}
                </Alert>
              ) : null}
            </div>
          ) : (
            <div className="psi-student-test__state-card">
              <div className="psi-student-test__state-icon psi-student-test__state-icon--success">
                <FontAwesomeIcon icon="check-circle" />
              </div>
              <h2 className="psi-student-test__state-title">Test yakunlandi</h2>
              <p className="psi-student-test__state-text">
                {resultError ||
                  "Javoblaringiz saqlandi. Kerak bo'lsa, talaba kirish sahifasiga qaytishingiz mumkin."}
              </p>
              <div className="psi-student-test__state-actions">
                <Button
                  color="primary"
                  className="psi-student-test__state-button"
                  onClick={() => handlePortalExit(LOGIN_ROUTE)}
                  disabled={exitPending}
                >
                  Talaba kirishiga qaytish
                </Button>
              </div>
            </div>
          )
        ) : null}

        {!loading && archived ? (
          <div className="psi-student-test__state-card">
            <div className="psi-student-test__state-icon psi-student-test__state-icon--danger">
              <FontAwesomeIcon icon="exclamation-triangle" />
            </div>
            <h2 className="psi-student-test__state-title">Test yopilgan</h2>
            <p className="psi-student-test__state-text">
              Ushbu test sessiyasi arxivlangan yoki cheklangan. Talaba kirishidan qayta tekshirib ko&apos;ring.
            </p>
            <div className="psi-student-test__state-actions">
              <Button
                color="light"
                className="psi-student-passport__ghost"
                onClick={() => handlePortalExit(LOGIN_ROUTE)}
                disabled={exitPending}
              >
                Orqaga qaytish
              </Button>
            </div>
          </div>
        ) : null}

        {!loading && !completed && !archived && questionState ? (
          <>
            <div className="psi-student-test__hero">
              <div className="psi-student-test__student">
                <div className="psi-student-test__photo">
                  <img
                    src={currentPhoto}
                    alt="Talaba rasmi"
                    onError={({ currentTarget }) => {
                      currentTarget.onerror = null;
                      currentTarget.src = defaultImage;
                    }}
                  />
                </div>

                <div className="psi-student-test__student-copy">
                  <span>Talaba profili</span>
                  <strong>{questionState.studentName || 'Talaba'}</strong>
                  <div className="psi-student-test__chips">
                    <Badge pill className="psi-student-test__chip psi-student-test__chip--primary">
                      <FontAwesomeIcon icon="book" className="mr-2" />
                      {questionState.subject || 'Fan mavjud emas'}
                    </Badge>
                    <Badge pill className="psi-student-test__chip psi-student-test__chip--soft">
                      <FontAwesomeIcon icon="users" className="mr-2" />
                      {questionState.group || 'Guruh mavjud emas'}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="psi-student-test__metrics">
                <div className="psi-student-test__metric">
                  <span className="psi-student-test__metric-icon">
                    <FontAwesomeIcon icon="list-alt" />
                  </span>
                  <div>
                    <strong>
                      {questionState.questionNumber}/{questionState.totalQuestions}
                    </strong>
                    <span>Joriy savol</span>
                  </div>
                </div>

                <div className="psi-student-test__metric">
                  <span className="psi-student-test__metric-icon">
                    <FontAwesomeIcon icon="clock" />
                  </span>
                  <div>
                    <strong>{formatClock(secondsLeft)}</strong>
                    <span>Qolgan vaqt</span>
                  </div>
                </div>

                <div className="psi-student-test__metric">
                  <span className="psi-student-test__metric-icon">
                    <FontAwesomeIcon icon={selectedVariant ? 'check-circle' : 'circle-notch'} />
                  </span>
                  <div>
                    <strong>{selectedVariant ? 'Tanlandi' : 'Kutilmoqda'}</strong>
                    <span>Javob holati</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="psi-student-test__progress">
              <div className="psi-student-test__progress-head">
                <strong>Savol jarayoni</strong>
                <span>{Math.round(progressPercent)}% bajarildi</span>
              </div>
              <div className="psi-student-test__progress-track">
                <span
                  className="psi-student-test__progress-bar"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className="psi-student-test__progress-dots" aria-hidden="true">
                {Array.from({ length: questionState.totalQuestions }).map((_, index) => {
                  const order = index + 1;
                  const className =
                    order < questionState.questionNumber
                      ? 'is-done'
                      : order === questionState.questionNumber
                        ? 'is-current'
                        : '';

                  return (
                    <span
                      key={`progress-${order}`}
                      className={`psi-student-test__progress-dot ${className}`.trim()}
                    >
                      {order}
                    </span>
                  );
                })}
              </div>
            </div>

            <form className="psi-student-test__workspace" onSubmit={handleSubmit}>
              <section className="psi-student-test__card psi-student-test__card--question">
                <div className="psi-student-test__card-meta">
                  <Badge pill className="psi-student-test__chip psi-student-test__chip--dark">
                    Savol {questionState.questionNumber}
                  </Badge>
                  <span>Hemis ID: {questionState.passport || 'Mavjud emas'}</span>
                </div>

                <div
                  className="psi-student-test__rich-content psi-student-test__rich-content--question"
                  dangerouslySetInnerHTML={{ __html: questionHtml }}
                />
              </section>

              <section className="psi-student-test__card psi-student-test__card--answers">
                <div className="psi-student-test__answer-head">
                  <div>
                    <strong>Javob variantlari</strong>
                    <span>Variantlardan birini tanlang va keyingi savolga o&apos;ting.</span>
                  </div>
                </div>

                <div className="psi-student-test__options">
                  {normalizedOptions.map(option => {
                    const active = selectedVariant === option.value;

                    return (
                      <button
                        key={`${option.key}-${option.value}`}
                        type="button"
                        className={`psi-student-test__option ${active ? 'is-active' : ''}`.trim()}
                        onClick={() => setSelectedVariant(option.value)}
                      >
                        <span className="psi-student-test__option-key">{option.key}</span>
                        <span
                          className="psi-student-test__rich-content psi-student-test__rich-content--answer"
                          dangerouslySetInnerHTML={{ __html: option.html }}
                        />
                        <span className="psi-student-test__option-check">
                          <FontAwesomeIcon icon={active ? 'check-circle' : 'chevron-right'} />
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="psi-student-test__footer">
                  <div className="psi-student-test__hint">
                    <FontAwesomeIcon icon="clock" />
                    <span>
                      {secondsLeft > 0
                        ? 'Savol ustida ishlayotganingizda vaqt ishlashda davom etadi.'
                        : 'Vaqt nolga yetdi, lekin yakuniy javobni yuborishingiz mumkin.'}
                    </span>
                  </div>

                  <Button
                    color="primary"
                    type="submit"
                    className="psi-student-test__submit"
                    disabled={!selectedVariant || submitting || exitPending}
                  >
                    {submitting ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        Yuborilmoqda...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon
                          icon={finalQuestion ? 'check-circle' : 'arrow-right'}
                          className="mr-2"
                        />
                        {finalQuestion ? 'Testni yakunlash' : 'Keyingi savol'}
                      </>
                    )}
                  </Button>
                </div>
              </section>
            </form>
          </>
        ) : null}

        {!loading && !completed && !archived && !questionState && !errorMessage ? (
          <div className="psi-student-test__state-card">
            <div className="psi-student-test__state-icon">
              <FontAwesomeIcon icon="book" />
            </div>
            <h2 className="psi-student-test__state-title">Savol topilmadi</h2>
            <p className="psi-student-test__state-text">
              Test uchun savollar tayyorlanmagan yoki sessiya muddati tugagan bo&apos;lishi mumkin.
            </p>
            <div className="psi-student-test__state-actions">
              <Button
                color="light"
                className="psi-student-passport__ghost"
                onClick={() => window.location.assign(TEST_ROUTE)}
              >
                Sahifani yangilash
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default StudentTestPage;
